import { getPageOrder } from './webDataPageOrder';
import { plainText } from './richTextHtml';

/**
 * Les cibles internes qu'un lien peut viser : les pages du site, et les blocs
 * de chaque page.
 *
 * Un bloc est ciblable par son `id`, qui est stable et ne se renomme jamais, ou
 * par l'ancre lisible que l'utilisateur lui a donnée. Les deux résolvent vers
 * la même page — c'est ce qui permet au viewer de changer de page avant de
 * défiler, alors qu'il ne connaissait jusqu'ici que les slugs de page et les
 * titres de niveau 2.
 *
 * Fonctions pures sur la structure `webData` : aucune dépendance à Puck ni au
 * DOM, elles servent aussi bien au sélecteur de la sidebar qu'au viewer publié.
 */

/** Blocs de premier niveau : eux seuls portent un id atteignable dans la page. */
function rootBlocks(page) {
  const content = page?.puckData?.content;
  return Array.isArray(content) ? content.filter((item) => item?.props?.id) : [];
}

/** Premier titre trouvé dans les colonnes du bloc, en parcourant ses zones. */
function firstTitleOf(page, blockId) {
  const zones = page?.puckData?.zones;
  if (!zones) return '';
  const prefix = `${blockId}:`;
  const boxes = Object.entries(zones)
    .filter(([key]) => key.startsWith(prefix))
    .flatMap(([, items]) => (Array.isArray(items) ? items : []));

  const title = boxes.find((box) => box?.type === 'ProposalTitle');
  if (title) {
    const text = plainText(title.props?.html);
    if (text) return text;
  }
  // À défaut d'un bloc Titre, le premier h2 d'un texte riche : c'est déjà ce
  // que la navigation du site utilise comme ancre.
  const rich = boxes.find((box) => (
    box?.type === 'ProposalRichText' && /<h2[\s>]/i.test(String(box.props?.html || ''))
  ));
  if (rich) {
    const match = String(rich.props.html).match(/<h2[^>]*>([\s\S]*?)<\/h2>/i);
    const text = plainText(match?.[1]);
    if (text) return text;
  }
  return '';
}

/**
 * Les blocs d'une page, prêts à être listés.
 *
 * @param {Object} page entrée de `webData.pages`
 * @param {(type: string) => string} [labelForType] libellé de repli d'un type
 * Le libellé suit ce que l'utilisateur a nommé : le titre du bloc, à défaut
 * l'ancre qu'il lui a donnée. Il est recalculé à chaque lecture, donc renommer
 * l'un ou l'autre se voit tout de suite dans le sélecteur.
 *
 * `position` et `hasName` sont là pour le cas restant : un bloc que personne
 * n'a nommé porte le libellé de repli de son type, et tous se ressembleraient
 * — il faut au moins pouvoir les compter.
 *
 * @returns {Array<{blockId: string, anchor: string, hash: string, label: string,
 *                  position: number, hasName: boolean}>}
 */
export function collectPageBlockTargets(page, labelForType) {
  return rootBlocks(page).map((item, index) => {
    const blockId = item.props.id;
    const anchor = item.props.anchor || '';
    const title = firstTitleOf(page, blockId);
    // Le titre d'abord — c'est ce qu'on lit sur la page. À défaut l'ancre :
    // quand un bloc n'a pas de titre, c'est le seul nom que l'utilisateur ait
    // pu lui donner, et il attend de le retrouver là.
    const named = title || anchor;
    return {
      blockId,
      anchor,
      // L'ancre lisible gagne quand elle existe : c'est elle qui apparaîtra
      // dans la barre d'adresse.
      hash: anchor || blockId,
      label: named || (labelForType ? labelForType(item.type) : item.type),
      position: index + 1,
      hasName: !!named,
    };
  });
}

/**
 * Toutes les cibles internes du site, groupées par page et dans l'ordre des
 * pages.
 *
 * @returns {Array<{pageSlug: string, pageTitle: string, blocks: Array}>}
 */
export function buildSiteLinkTargets(webData, labelForType) {
  const pages = webData?.pages || {};
  return getPageOrder(webData)
    .filter((slug) => pages[slug])
    .map((slug) => ({
      pageSlug: slug,
      pageTitle: pages[slug].title || slug,
      blocks: collectPageBlockTargets(pages[slug], labelForType),
    }));
}

/**
 * Index `id ou ancre → slug de page`, pour que le viewer sache quelle page
 * ouvrir avant de défiler vers un bloc.
 *
 * Deux blocs portant la même ancre : le premier dans l'ordre des pages gagne,
 * comme un `getElementById` sur un id dupliqué.
 */
export function buildBlockPageIndex(webData) {
  const index = {};
  buildSiteLinkTargets(webData).forEach(({ pageSlug, blocks }) => {
    blocks.forEach(({ blockId, anchor }) => {
      if (!(blockId in index)) index[blockId] = pageSlug;
      if (anchor && !(anchor in index)) index[anchor] = pageSlug;
    });
  });
  return index;
}
